import { useResponsiveProp, useTheme } from "@shopify/restyle";
import React from "react";
import { Pressable, Image } from "react-native";

import {
  Box,
  Text,
  HStack,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { getThumbnail } from "@whoosh/utilities/src/Cloudinary";
import { CustomColor } from "@whoosh/utilities/src/colors";

import { EventPlaceholderImage } from "../../../../components/display/EventPlaceholderImage/EventPlaceholderImage";
import {
  ClockIcon,
  CalendarFilledIcon,
  PlaceIcon,
} from "../../../../components/icons_v2/general";
import { Theme } from "../../../../theme/restyle/theme";
import { RestyleColorString } from "../../../../theme/types";
import { EventStatusNew, EventStatusType } from "./EventStatusNew";
import { EventCardNavigation } from "./components/EventCardNavigation/EventCardNavigation";

type DepartmentType =
  | "AQUATICS"
  | "BASKETBALL"
  | "BOATING"
  | "BUSINESS"
  | "CARD_ROOM"
  | "CLUB"
  | "CROQUET"
  | "DINING"
  | "FITNESS"
  | "GOLF"
  | "RACQUET"
  | "SALON"
  | "SPA"
  | "AXE_THROWING"
  | "BOCCE_BALL"
  | "HOCKEY"
  | "SHUFFLEBOARD";

export type EventCardProps = {
  subTitle: string;
  eventDate: string;
  eventTime: string;
  eventStatus: EventStatusType;
  eventColor: CustomColor;
  departmentType: DepartmentType;
  departmentName: string;
  locationLabel?: string;
  onPress?: () => void;
  spotsAvailable?: number;
  publicId?: string;
  opensAt?: string;
  isFeatured?: boolean;
  tags?: string[];
  eventSessionId?: string | null;
  pairingsPublished?: boolean;
};

export const EventCard = ({
  subTitle,
  onPress,
  eventDate,
  eventTime,
  eventStatus,
  eventColor,
  spotsAvailable,
  publicId,
  opensAt,
  departmentName,
  locationLabel,
  isFeatured,
  departmentType,
  tags,
  eventSessionId,
  pairingsPublished = false,
}: EventCardProps) => {
  const surfaceColorString: RestyleColorString = `surface-user-selectable-color${
    eventColor ?? "A"
  }-subtle`;

  const maxWidthHeight = 400;

  const isMobile = useResponsiveProp({
    base: 1,
    md: 0,
  });

  const iconColor = useTheme<Theme>().colors["icon-subtle"];
  return (
    <Pressable
      onPress={pairingsPublished ? undefined : onPress}
      style={{ flexShrink: 0, flexGrow: 1 }}
    >
      <Box
        borderRadius={"4"}
        flexShrink={0}
        flexGrow={1}
        bg={surfaceColorString}
        maxWidth={760}
      >
        <Box
          p={3}
          bg="surface-primary"
          m={1}
          style={{ borderRadius: 12 }}
          flexShrink={0}
          flexGrow={1}
        >
          <Box
            alignItems="flex-start"
            width="100%"
            flexDirection={isFeatured ? "column" : "row"}
            gap={isFeatured ? 0 : 4}
            testID="event-card-content"
          >
            {/* Event Image */}
            {isFeatured || !isMobile ? (
              <>
                {publicId ? (
                  <Image
                    style={{
                      width: "100%",
                      height: 84,
                      borderRadius: 8,
                      maxWidth: 154,
                    }}
                    source={{
                      uri: getThumbnail(
                        publicId,
                        maxWidthHeight,
                        maxWidthHeight
                      )?.toURL(),
                    }}
                  />
                ) : (
                  <EventPlaceholderImage
                    eventColor={eventColor}
                    departmentType={departmentType}
                    width={154}
                    height={84}
                    borderRadius="2"
                  />
                )}
              </>
            ) : null}
            {/* Event Details */}
            <VStack gap={2} width="100%" flexShrink={1}>
              <VStack mt={isFeatured ? 2 : 0}>
                <EventStatusNew
                  eventStatus={eventStatus}
                  spotsAvailable={spotsAvailable}
                  opensAt={opensAt}
                />

                {subTitle ? (
                  <HStack
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                  >
                    <Text variant="mainBaseSemiBold" numberOfLines={2}>
                      {subTitle || ""}
                    </Text>
                    <Box
                      width={8}
                      height={8}
                      bg={`text-user-selectable-color${eventColor ?? "C"}`}
                      borderRadius="0.5"
                      ml={2}
                    />
                  </HStack>
                ) : null}
              </VStack>
              <HStack alignItems="center" gap={2} flexWrap="wrap">
                <HStack gap={1}>
                  <Box
                    width={16}
                    height={16}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <CalendarFilledIcon color={iconColor} />
                  </Box>
                  <Text variant="mainTiny">{eventDate}</Text>
                </HStack>

                <HStack gap={1}>
                  <Box
                    width={16}
                    height={16}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <ClockIcon color={iconColor} />
                  </Box>
                  <Text variant="mainTiny">{eventTime}</Text>
                </HStack>
                {locationLabel ? (
                  <HStack gap={1}>
                    <Box
                      width={16}
                      height={16}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <PlaceIcon color={iconColor} />
                    </Box>
                    <Text variant="mainTiny">{locationLabel}</Text>
                  </HStack>
                ) : null}
              </HStack>
              {/* Department Name */}
              <HStack flexWrap="wrap" gap={1}>
                <Box
                  bg="surface-secondary"
                  height={20}
                  justifyContent="center"
                  paddingHorizontal={2}
                  borderRadius="1"
                  borderWidth={1}
                  borderColor="outline-secondary"
                >
                  <Text variant="mainTiny">{departmentName}</Text>
                </Box>
                {tags?.map((tag) => (
                  <Box
                    key={tag}
                    bg="surface-primary"
                    borderWidth={1}
                    borderColor="outline-default"
                    height={20}
                    justifyContent="center"
                    paddingHorizontal={2}
                    borderRadius="1"
                  >
                    <Text variant="mainTiny">{tag}</Text>
                  </Box>
                ))}
              </HStack>
            </VStack>
          </Box>
          {pairingsPublished ? (
            <EventCardNavigation
              onViewEvent={onPress}
              eventSessionId={eventSessionId}
            />
          ) : null}
        </Box>
      </Box>
    </Pressable>
  );
};
